import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import cx from "classnames";

import style from "./Header.module.css";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header: React.FC = () => {
  const [navIsOpen, setNavIsOpen] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    setNavIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (navIsOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [navIsOpen]);

  return (
    <>
      <nav
        className={cx(
          "top-0 z-50 flex items-center justify-between w-full px-6 h-14 bg-header-pattern backdrop-blur-xl border-b border-gray-200",
          style.header
        )}
      >
        <Link
          to="/"
          className="font-bold font-title text-lg text-gray-900 hover:text-green-600 transition-colors"
        >
          JD
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-8">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={cx(
                "text-sm font-medium transition-colors",
                location.pathname === to
                  ? "text-green-600"
                  : "text-gray-600 hover:text-gray-900"
              )}
            >
              {label}
            </Link>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className={cx("sm:hidden relative w-6 h-5 flex flex-col justify-between", style.hamburger, navIsOpen && style.hamburgerOpen)}
          onClick={() => setNavIsOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          <span className="block w-full h-0.5 bg-gray-900 transition-all duration-300 origin-center" />
          <span className="block w-full h-0.5 bg-gray-900 transition-all duration-300" />
          <span className="block w-full h-0.5 bg-gray-900 transition-all duration-300 origin-center" />
        </button>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {navIsOpen && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm sm:hidden"
            onClick={() => setNavIsOpen(false)}
          >
            <m.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl pt-20 px-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-6">
                {navLinks.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setNavIsOpen(false)}
                    className={cx(
                      "text-lg font-medium font-body transition-colors",
                      location.pathname === to
                        ? "text-green-600"
                        : "text-gray-700 hover:text-green-600"
                    )}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
